

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import {
  FaHome, FaUsers, FaUsersCog, FaEnvelopeOpenText, FaDonate,
  FaWallet, FaCreditCard, FaChartPie, FaChartBar, FaGlobe,
  FaSms, FaCog, FaChevronLeft, FaChevronRight, FaImage, FaSignOutAlt
} from 'react-icons/fa';
import { useAuthContext } from '../../../context/AuthContext';
import authApi from '../../../api/auth/authApi';
import { STORAGE_KEYS } from '../../../api/config';

import './style/Sidebar.css';
import logo from './assets/logo.png';

const Sidebar = ({ isOpen, toggleSidebar, collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuthContext();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Don't show sidebar if not authenticated
  if (!isAuthenticated || loading) {
    return null;
  }

  const handleToggleCollapse = () => {
    onToggleCollapse();
  };

  const handleLinkClick = () => {
    if (window.innerWidth < 768) {
      toggleSidebar();
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.UID);
      localStorage.removeItem(STORAGE_KEYS.EVENT_ID);
      localStorage.removeItem('selectedEventId');
      setShowLogoutModal(false);
      navigate('/login');
    }
  };

  // Wedding/Event Planning themed menu items
  const menuItems = [
    { to: "/dashboard", icon: FaHome, label: "Dashboard" },
    { to: "/wageni", icon: FaUsers, label: "Wageni" },
    { to: "/kamati", icon: FaUsersCog, label: "Kamati" },
    { to: "/waalikwa", icon: FaEnvelopeOpenText, label: "Waalikwa" },
    { to: "/michango", icon: FaDonate, label: "Michango" },
    { to: "/bajeti", icon: FaChartPie, label: "Bajeti" },
    { to: "/payments", icon: FaCreditCard, label: "Payments" },

    { to: "/takwimu", icon: FaChartBar, label: "Takwimu" },
    { to: "/picha", icon: FaImage, label: "Picha" },
    { to: "/ecards", icon: FaEnvelopeOpenText, label: "Ecards" },
    { to: "/sms-history", icon: FaSms, label: "SMS History" },
    { to: "/Website-Templates", icon: FaGlobe, label: "Website" },
    { to: "/settings", icon: FaCog, label: "Mipangilio" },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      <div className={`sidebar-container ${isOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-glass">
          {/* Sidebar Header */}
          <div className="sidebar-header">
            <div className="logo-wrapper">
              <div className="logo-container">
                <img src={logo} alt="Harusi Online Logo" className="sidebar-logo" />
                {!collapsed && (
                  <div className="logo-text">
                    <h3>Harusi Online</h3>
                    <p>Admin Panel</p>
                  </div>
                )}
              </div>
            </div>
            <button
              className="sidebar-toggle"
              onClick={handleToggleCollapse}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
            </button>
          </div>

          {/* Navigation Menu */}
          <nav className="sidebar-nav">
            <ul className="nav-menu">
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                const active = isActive(item.to);

                return (
                  <li key={index} className="nav-item-wrapper">
                    <Link
                      to={item.to}
                      className={`nav-item ${active ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`}
                      data-tour={item.label ? item.label.toLowerCase().replace(/\s+/g, '-') : undefined}
                      onClick={handleLinkClick}
                      title={collapsed ? item.label : ''}
                    >
                      <div className="nav-icon-container">
                        <Icon className="nav-icon" />
                        {!collapsed && <span className="nav-label">{item.label}</span>}
                      </div>
                      {active && <div className="active-indicator"></div>}
                    </Link>
                  </li>
                );
              })}

              {/* Logout Menu Item */}
              <li className="nav-item-wrapper mt-2">
                <div
                  className={`nav-item ${collapsed ? 'collapsed' : ''}`}
                  style={{ cursor: 'pointer', color: '#ef4444' }}
                  onClick={() => setShowLogoutModal(true)}
                  title={collapsed ? 'Toka (Logout)' : ''}
                >
                  <div className="nav-icon-container">
                    <FaSignOutAlt className="nav-icon" style={{ color: '#ef4444' }} />
                    {!collapsed && <span className="nav-label" style={{ color: '#ef4444', fontWeight: '600' }}>Toka (Logout)</span>}
                  </div>
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}

      {/* Logout Confirmation Modal */}
      <Modal
        show={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        centered
        className="logout-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h5 fw-bold">Logout Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center pt-2 pb-4">
          <div className="logout-icon-wrapper mb-3">
            <FaSignOutAlt className="text-danger display-4" />
          </div>
          <p className="mb-0 text-muted">Are you sure you want to log out of the system?</p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 justify-content-center">
          <Button variant="light" className="px-4 pill-btn" onClick={() => setShowLogoutModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" className="px-4 pill-btn" onClick={handleLogout}>
            Yes, Log out
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Sidebar;